import type { ReactNode } from 'react';
import { Button, Card, Coin } from '@/components/ui';
import { useAuth } from '@/lib/auth';
import { useWallet, rupees } from '@/lib/data/wallet';

/**
 * The one-time platform fee, as a gate on browsing companions.
 *
 * Like RequireAuth, THIS IS NOT THE BOUNDARY. Anyone can edit the JS. The real
 * gate is RLS: migration 0021 puts `may_browse_companions()` on the SELECT
 * policies for `companion_profiles` and `profiles`, so an unpaid account gets
 * zero rows however it asks. This component exists so that a person sees an
 * explanation and a way forward instead of an empty directory that looks
 * broken.
 *
 * Which is exactly why it renders the paywall rather than a redirect: bouncing
 * someone to /wallet from a link they followed loses the thing they were
 * trying to look at.
 *
 * Admins and moderators are not gated — they have their own SELECT policies in
 * the database and would otherwise be locked out of the product they run.
 */
export default function RequireBrowseFee({ children }: { children: ReactNode }) {
  const { profile } = useAuth();
  const { wallet, loading } = useWallet();

  const isStaff = profile?.role === 'admin' || profile?.role === 'moderator';

  // Never flash the paywall while the wallet is still loading — someone who
  // has paid should not see a demand for money on every refresh.
  if (loading || !wallet || isStaff) return <>{children}</>;

  const { userFeePaid, userFeePaise } = wallet.fees;

  // A fee of zero is a fee switched off. Mirrors may_browse_companions() in
  // 0021, which returns true when the setting is 0 — the two must agree or the
  // page renders against rows the database will not hand over.
  if (userFeePaid || userFeePaise === 0) return <>{children}</>;

  return (
    <div className="mx-auto max-w-lg px-4 py-16">
      <Card className="animate-rise px-6 py-10 text-center">
        <span
          className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl"
          style={{ background: 'var(--marigold-soft)', color: 'var(--marigold)' }}
        >
          <Coin />
        </span>

        <h1 className="text-2xl font-semibold">A one-time fee unlocks the directory</h1>

        <p className="mt-3 text-[var(--ink-2)]">
          {rupees(userFeePaise)}, paid once. It covers verification of every companion on the
          platform — background checks, ID, and review before anyone is listed.
        </p>

        <ul className="mx-auto mt-6 flex max-w-xs flex-col gap-2 text-left text-sm text-[var(--ink-2)]">
          {[
            'Browse every verified companion',
            'See rates, languages and availability',
            'Book by chat, call or in person',
          ].map((line) => (
            <li key={line} className="flex items-start gap-2">
              <span style={{ color: 'var(--teal)' }}>✓</span>
              {line}
            </li>
          ))}
        </ul>

        <div className="mt-8 flex flex-col gap-3">
          <Button to="/wallet" full>
            Pay {rupees(userFeePaise)} once
          </Button>
          <Button to="/services" variant="ghost">
            See what services exist first
          </Button>
        </div>

        <p className="mt-6 text-xs text-[var(--muted)]">
          Paid once, never again. Coins for bookings are bought separately.
        </p>
      </Card>
    </div>
  );
}
